export default class LabelPrintService {
  constructor (http, store, router) {
    this.$http = http
    this.$store = store
    this.$router = router
  }

  /**
   * Generate labels with proper layout and page breaks
   * @param {Array} items - Array of items to generate labels for
   * @param {Object} config - Label configuration
   * @returns {Array} - Array of label objects
   */
  generateLabels (items, config) {
    const labels = []

    items.forEach(item => {
      const barcodeValue = this.getBarcodeValue(item, config.barcode_source_field)

      // Generate labels even if no barcode (for name/price only labels)
      const shouldGenerate = barcodeValue || (config.print_name && item.name) || (config.print_price && item.unit_price)

      if (shouldGenerate) {
        for (let i = 0; i < item.quantity; i++) {
          labels.push({
            id: item.id,
            name: item.name || '',
            price: item.unit_price || 0,
            barcode: barcodeValue || '',
            sku: item.code || '',
            department: config.department
          })
        }
      }
    })

    return labels
  }

  /**
   * Get barcode value based on source field
   * @param {Object} item - Item object
   * @param {String} sourceField - Source field for barcode
   * @returns {String} - Barcode value
   */
  getBarcodeValue (item, sourceField) {
    switch (sourceField) {
      case 'code':
        return item.code || ''
      case 'barcode':
        return item.barcode || ''
      case 'none':
        return ''
      default:
        return item.code || item.barcode || ''
    }
  }

  /**
   * Calculate optimal label layout for printing
   * @param {String} labelType - Label type dimensions
   * @param {Number} pageWidth - Page width in mm
   * @param {Number} pageHeight - Page height in mm
   * @returns {Object} - Layout configuration
   */
  calculateLayout (labelType, pageWidth = 210, pageHeight = 297) {
    const dimensions = labelType.split(' x ')
    if (dimensions.length !== 2) {
      return { columns: 3, rows: 8, gap: 5 }
    }

    const labelWidth = parseInt(dimensions[0].replace('mm', ''))
    const labelHeight = parseInt(dimensions[1].replace('mm', ''))

    const columns = Math.floor((pageWidth - 20) / (labelWidth + 5))
    const rows = Math.floor((pageHeight - 20) / (labelHeight + 5))

    return {
      columns: Math.max(1, columns),
      rows: Math.max(1, rows),
      gap: 5,
      labelWidth,
      labelHeight
    }
  }

  /**
   * Validate label configuration
   * @param {Object} config - Label configuration
   * @returns {Object} - Validation result
   */
  validateConfig (config) {
    const errors = []

    if (!config.label_type) {
      errors.push('Label type is required')
    }

    if (!config.department) {
      errors.push('Department is required')
    }

    if (!config.barcode_source_field) {
      errors.push('Barcode source field is required')
    }

    if (!config.barcode_source_field || config.barcode_source_field === 'none') {
      if (!config.print_name) {
        errors.push('At least one display option (name or barcode) must be selected')
      }
    }

    return {
      valid: errors.length === 0,
      errors
    }
  }

  /**
   * Format price for display
   * @param {Number} price - Price value
   * @param {String} currency - Currency code
   * @returns {String} - Formatted price
   */
  formatPrice (price, currency = 'XAF') {
    if (!price || isNaN(price)) return ''

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price)
  }

  /**
   * Get barcode options based on label type
   * @param {String} labelType - Label type
   * @returns {Object} - Barcode options
   */
  getBarcodeOptions (labelType) {
    const dimensions = labelType.split(' x ')
    if (dimensions.length !== 2) {
      return {
        displayValue: true,
        width: 1.5,
        height: 40,
        fontSize: 12,
        textMargin: 2,
        margin: 1,
        background: '#ffffff',
        lineColor: '#000000'
      }
    }

    const labelWidth = parseInt(dimensions[0].replace('mm', ''))
    const labelHeight = parseInt(dimensions[1].replace('mm', ''))

    // Calculate optimal barcode size based on label dimensions
    const maxBarcodeWidth = labelWidth - 4 // 2mm margin on each side
    const maxBarcodeHeight = labelHeight * 0.5 // 50% of label height for barcode

    return {
      displayValue: true,
      width: Math.max(1, Math.min(2, maxBarcodeWidth / 50)), // Adjust width based on label size
      height: Math.max(20, Math.min(50, maxBarcodeHeight)),
      fontSize: Math.max(8, Math.min(14, labelHeight * 0.12)),
      textMargin: 2,
      margin: 1,
      background: '#ffffff',
      lineColor: '#000000'
    }
  }

  /**
   * Prepare print styles for optimal printing
   * @param {String} labelType - Label type
   * @returns {String} - CSS styles
   */
  getPrintStyles (labelType) {
    const layout = this.calculateLayout(labelType)

    return `
      @media print {
        @page {
          margin: 10mm;
          size: A4;
        }

        body {
          margin: 0;
          padding: 0;
        }

        .print-container {
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .labels-grid {
          display: grid !important;
          grid-template-columns: repeat(${layout.columns}, 1fr) !important;
          gap: ${layout.gap}mm !important;
          page-break-inside: avoid !important;
        }

        .label-item {
          page-break-inside: avoid !important;
          page-break-after: auto !important;
          border: 1px solid #000 !important;
          padding: 2mm !important;
          margin: 0 !important;
          width: ${layout.labelWidth}mm !important;
          height: ${layout.labelHeight}mm !important;
          box-sizing: border-box !important;
        }

        .label-content {
          text-align: center !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          height: 100% !important;
        }

        .label-price {
          font-size: ${Math.max(8, layout.labelHeight * 0.12)}pt !important;
          font-weight: bold !important;
          margin-bottom: 1mm !important;
        }

        .label-name {
          font-size: ${Math.max(6, layout.labelHeight * 0.1)}pt !important;
          margin-bottom: 2mm !important;
          word-wrap: break-word !important;
          line-height: 1.2 !important;
        }

        .label-barcode {
          text-align: center !important;
          flex-grow: 1 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        .label-barcode canvas {
          max-width: 100% !important;
          max-height: 100% !important;
          height: auto !important;
        }
      }
    `
  }

  /**
   * Print labels with proper page management
   * @param {Array} labels - Labels to print
   * @param {Object} config - Print configuration
   */
  async printLabels (labels, config) {
    return new Promise((resolve, reject) => {
      try {
        // Create a new window for printing
        const printWindow = window.open('', '_blank', 'width=800,height=600')

        if (!printWindow) {
          throw new Error('Popup blocked. Please allow popups for this site.')
        }

        // Create the print HTML with proper styling
        const printHTML = this.createPrintHTML(labels, config)

        // Write the HTML to the new window
        printWindow.document.write(printHTML)
        printWindow.document.close()

        // Wait for content to load, then print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print()
            // Don't automatically close the window
            resolve()
          }, 500)
        }

        // Fallback if onload doesn't fire
        setTimeout(() => {
          if (!printWindow.closed) {
            printWindow.print()
            // Don't automatically close the window
            resolve()
          }
        }, 2000)
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Create print HTML with proper styling
   * @param {Array} labels - Labels to print
   * @param {Object} config - Print configuration
   * @returns {String} - HTML string for printing
   */
  createPrintHTML (labels, config) {
    const layout = this.calculateLayout(config.label_type)

    // Generate label HTML
    const labelsHTML = labels.map((label, index) => {
      const barcodeHTML = label.barcode
        ? '<div class="label-barcode"><svg id="barcode-' + index + '" class="barcode-svg"></svg></div>'
        : ''

      const priceHTML = config.print_price && label.price
        ? '<div class="label-price">' + this.formatPrice(label.price) + '</div>'
        : ''

      const nameHTML = config.print_name && label.name
        ? '<div class="label-name">' + label.name + '</div>'
        : ''

      return [
        '<div class="label-item">',
        '<div class="label-content">',
        priceHTML,
        nameHTML,
        barcodeHTML,
        '</div>',
        '</div>'
      ].join('')
    }).join('')

    // Generate barcode script
    const barcodeScript = labels.map((label, index) => {
      if (!label.barcode) return ''
      const options = this.getBarcodeOptions(config.label_type)
      return 'JsBarcode("#barcode-' + index + '", "' + label.barcode + '", {' +
        'displayValue: ' + (options.displayValue ? 'true' : 'false') + ',' +
        'width: ' + options.width + ',' +
        'height: ' + options.height + ',' +
        'fontSize: ' + options.fontSize + ',' +
        (options.textMargin != null ? ('textMargin: ' + options.textMargin + ',') : '') +
        'margin: ' + options.margin + ',' +
        'background: "' + options.background + '",' +
        'lineColor: "' + options.lineColor + '"' +
        '});'
    }).join('')

    return [
      '<!DOCTYPE html>',
      '<html>',
      '<head>',
      '<title>Labels Print</title>',
      '<meta charset="utf-8">',
      '<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>',
      '<style>',
      '@page { margin: 10mm; size: A4; }',
      'body { margin: 0; padding: 0; font-family: Arial, sans-serif; background: white; }',
      '.print-container { width: 100%; margin: 0; padding: 0; background: white; }',
      '.labels-grid { display: grid; grid-template-columns: repeat(' + layout.columns + ', 1fr); gap: ' + layout.gap + 'mm; width: 100%; page-break-inside: avoid; }',
      '.label-item { border: 1px solid #000; padding: 2mm; margin: 0; page-break-inside: avoid; background: white; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: ' + layout.labelHeight + 'mm; box-sizing: border-box; overflow: hidden; }',
      '.label-content { text-align: center; display: flex; flex-direction: column; justify-content: space-between; align-items: center; height: 100%; width: 100%; }',
      '.label-price { font-size: ' + Math.max(8, layout.labelHeight * 0.12) + 'pt; font-weight: bold; margin-bottom: 1mm; color: #000; }',
      '.label-name { font-size: ' + Math.max(6, layout.labelHeight * 0.1) + 'pt; margin-bottom: 2mm; word-wrap: break-word; line-height: 1.2; color: #000; max-width: 100%; }',
      '.label-barcode { text-align: center; flex-grow: 1; display: flex; align-items: center; justify-content: center; width: 100%; }',
      '.barcode-svg { max-width: 100%; max-height: 100%; height: auto; }',
      '@media print { .label-item { page-break-inside: avoid; } }',
      '</style>',
      '</head>',
      '<body>',
      '<div class="print-container">',
      '<div class="labels-grid">',
      labelsHTML,
      '</div>',
      '</div>',
      '<script>',
      'window.onload = function() {',
      barcodeScript,
      '};',
      '</script>',
      '</body>',
      '</html>'
    ].join('')
  }

  /**
   * Export labels as PDF (if needed in future)
   * @param {Array} labels - Labels to export
   * @param {Object} config - Export configuration
   */
  async exportAsPDF (labels, config) {
    // This could be implemented with jsPDF or similar library
    // For now, we'll use the print functionality
    return this.printLabels(labels, config)
  }
}
